import { useCallback, useRef } from "react";

import {
  EhrActionRequestDocument,
  EhrActionRequestQuery,
  EhrActionRequestQueryVariables,
  EhrRequestActionDocument,
  EhrRequestActionMutation,
  EhrRequestActionMutationVariables,
} from "@api/graphql/types-and-hooks";
import { useCustomFetcher } from "@api/graphql/useCustomFetcher";

/** Poll cadence and ceiling for a queued org-session action.
 *
 * A LOGIN can legitimately take minutes — the CentralReach login pays a
 * captcha solve (~210s, occasionally twice) before it resolves — so the
 * ceiling is generous. Anything past it means the session never picked the
 * request up, which is a real failure worth surfacing.
 */
const POLL_INTERVAL_MS = 3000;
/** Consecutive poll failures tolerated before giving up on the request. */
const MAX_CONSECUTIVE_POLL_ERRORS = 3;
const MAX_POLL_MS = 10 * 60 * 1000;
/** Outcome of a queued action, as this hook reports it to callers. */
/** The actions the org session knows how to run. */
export type OrgSessionActionType = "LOGIN" | "IMPORT_CLIENTS" | "GET_TREATMENT_PLAN" | "CREATE_PROGRAMS";

export const ORG_SESSION_STATUS = {
  SUCCEEDED: "SUCCEEDED",
  FAILED: "FAILED",
  TIMED_OUT: "TIMED_OUT",
  CANCELLED: "CANCELLED",
} as const;

export type OrgSessionActionStatus = (typeof ORG_SESSION_STATUS)[keyof typeof ORG_SESSION_STATUS];

const TERMINAL: string[] = [ORG_SESSION_STATUS.SUCCEEDED, ORG_SESSION_STATUS.FAILED];

export interface OrgSessionActionResult {
  status: OrgSessionActionStatus;
  /** Reason from the request row — the real cause (bad credentials, MFA,
   * unreachable EHR), not generic copy. */
  error?: string | null;
  requestId?: number;
  /** False when an identical request was already in flight. The existing one
   * is polled instead; the action is org-global, so someone else's run
   * satisfies this caller too. */
  created?: boolean;
}

/** Queue an action on the org-wide EHR session and wait for the outcome (INT-36).
 *
 * The org session writes no EhrConnectionStatus and no learner import status
 * while work is in flight, so `EhrActionRequest` is the only place progress
 * and failure reasons exist. Every caller polls the request row rather than
 * the connection badge — polling the badge would see a steady CONNECTED and
 * report success before the work had run.
 */
/** Poll one queued request until it reaches a terminal status.
 *
 * Module-level rather than inline in the hook: it is the only branching
 * part of this file, and folding it into the hook body pushed the hook
 * over the cognitive-complexity limit.
 */
async function pollUntilTerminal({
  fetchActionRequest,
  requestId,
  created,
  isCancelled,
}: {
  fetchActionRequest: (vars: EhrActionRequestQueryVariables) => Promise<EhrActionRequestQuery>;
  requestId: number;
  created?: boolean;
  isCancelled: () => boolean;
}): Promise<OrgSessionActionResult> {
  const deadline = Date.now() + MAX_POLL_MS;
  let consecutiveErrors = 0;
  while (Date.now() < deadline) {
    if (isCancelled()) return { status: ORG_SESSION_STATUS.CANCELLED, requestId, created };

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    if (isCancelled()) return { status: ORG_SESSION_STATUS.CANCELLED, requestId, created };

    // A single transient failure must not end the flow: at a 3s cadence
    // over the ceiling this runs up to ~200 times, and a 502 from the ALB
    // or a dropped connection on tab resume says nothing about the queued
    // action, which is still running server-side.
    let request;
    try {
      request = (await fetchActionRequest({ requestId })).ehrActionRequest;
      consecutiveErrors = 0;
    } catch (error) {
      consecutiveErrors += 1;
      if (consecutiveErrors > MAX_CONSECUTIVE_POLL_ERRORS) throw error;
      continue;
    }
    if (!request || !TERMINAL.includes(request.status)) continue;

    return {
      status:
        request.status === ORG_SESSION_STATUS.SUCCEEDED ? ORG_SESSION_STATUS.SUCCEEDED : ORG_SESSION_STATUS.FAILED,
      error: request.error,
      requestId,
      created,
    };
  }
  return { status: ORG_SESSION_STATUS.TIMED_OUT, requestId, created };
}

export const useOrgSessionAction = () => {
  // Plain fetchers rather than the generated react-query hooks: those call
  // useMutation/useQuery, which would require every component rendering this
  // hook to sit inside a QueryClientProvider. This hook is used from dialogs
  // and plain hooks that legitimately do not.
  const requestEhrAction = useCustomFetcher<EhrRequestActionMutation, EhrRequestActionMutationVariables>(
    EhrRequestActionDocument,
  );
  const fetchActionRequest = useCustomFetcher<EhrActionRequestQuery, EhrActionRequestQueryVariables>(
    EhrActionRequestDocument,
  );
  const cancelledRef = useRef(false);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  /** Queue the action and return as soon as the row exists.
   *
   * For callers that report progress somewhere other than the dialog (the
   * badge + "Connecting to your EHR..." toast), matching what the
   * per-provider path did on "Login Started". A rejected mutation still
   * throws, so an unauthorised or disconnect-first attempt surfaces inline
   * instead of being hidden behind a toast.
   */
  const queue = useCallback(
    async (variables: {
      actionType: OrgSessionActionType;
      learnerId?: string | null;
      reportId?: string | null;
      username?: string | null;
      password?: string | null;
    }): Promise<{ requestId?: number; created?: boolean; message?: string | null }> => {
      const queued = await requestEhrAction(variables);
      return {
        requestId: queued.ehrRequestAction?.requestId ?? undefined,
        created: queued.ehrRequestAction?.created ?? undefined,
        message: queued.ehrRequestAction?.message,
      };
    },
    [requestEhrAction],
  );

  const queueAndWait = useCallback(
    async (variables: {
      actionType: OrgSessionActionType;
      learnerId?: string | null;
      reportId?: string | null;
      username?: string | null;
      password?: string | null;
    }): Promise<OrgSessionActionResult> => {
      cancelledRef.current = false;

      // A rejected mutation (not an admin, disconnect required, another
      // admin already connecting) throws with a message meant for the user
      // — let it reach the caller rather than flattening it to a status.
      const { requestId, created, message } = await queue(variables);
      if (!requestId) {
        return { status: ORG_SESSION_STATUS.FAILED, error: message, created };
      }
      return pollUntilTerminal({
        fetchActionRequest,
        requestId,
        created,
        isCancelled: () => cancelledRef.current,
      });
    },
    [fetchActionRequest, queue],
  );

  return { queue, queueAndWait, cancel };
};
